import type { Journey, JourneyStep } from "../api";
import {
  ambassadorGrades,
  ambassadorTypes,
  compensationComponents,
  growthAiDemo,
  steps as stepCopy,
  ui,
  workspaceDemo,
  type NamedItem,
} from "../content/en";
import { interpolate, money, percent, roleName, territoryName } from "./format";

interface Props {
  readonly journey: Journey;
  readonly step: JourneyStep;
  readonly vars: Readonly<Record<string, string>>;
}

function Cards({
  items,
  vars,
}: {
  readonly items: readonly NamedItem[];
  readonly vars: Readonly<Record<string, string>>;
}): React.JSX.Element {
  return (
    <ul className="cards">
      {items.map((item) => (
        <li className="card" key={item.title}>
          <strong>{interpolate(item.title, vars)}</strong>
          {item.detail !== undefined && (
            <span>{interpolate(item.detail, vars)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Network({
  vars,
  roles,
}: {
  readonly vars: Readonly<Record<string, string>>;
  readonly roles: readonly string[];
}): React.JSX.Element {
  return (
    <ul className="hierarchy" aria-label="Network structure">
      <li data-depth="0">
        Zhatura
        <span className="depth-note">Appoints Growth Partners</span>
      </li>
      {roles.map((role, index) => (
        <li key={role} data-depth={String(index + 1)}>
          {roleName(role, index === 0 && vars.Founding === "true")}
          {index === 0 && (
            <span className="depth-note">{vars.Territory ?? ""}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function Economics({
  journey,
  vars,
}: {
  readonly journey: Journey;
  readonly vars: Readonly<Record<string, string>>;
}): React.JSX.Element {
  const terms = journey.program.compensation;
  const maximum = Math.max(
    ...terms.direct_commission.map((band) => band.rate_basis_points),
    1,
  );
  const last = terms.direct_commission.at(-1);
  return (
    <>
      <ul className="bands" aria-label="Direct commission by subscription year">
        {terms.direct_commission.map((band) => (
          <li className="band" key={band.subscription_year}>
            <span className="band-year">
              {interpolate(
                band.rate_basis_points === 0 && last === band
                  ? ui.yearOnwards
                  : ui.yearLabel,
                { year: String(band.subscription_year) },
              )}
            </span>
            <span className="band-bar" aria-hidden="true">
              <span
                style={{
                  width: `${String((band.rate_basis_points / maximum) * 100)}%`,
                }}
              />
            </span>
            <span className="band-rate">{percent(band.rate_basis_points)}</span>
          </li>
        ))}
      </ul>
      <section className="example" aria-labelledby="example-title">
        <h2 id="example-title" className="section-title">
          {ui.exampleTitle}
        </h2>
        <p>
          {interpolate(ui.exampleLede, {
            revenue: money(
              terms.illustration_annual_revenue_minor,
              terms.currency,
            ),
          })}
        </p>
        <dl>
          {terms.direct_commission
            .filter((band) => band.rate_basis_points > 0)
            .map((band) => (
              <div key={band.subscription_year} style={{ display: "contents" }}>
                <dt>
                  {interpolate(ui.yearLabel, {
                    year: String(band.subscription_year),
                  })}{" "}
                  · {percent(band.rate_basis_points)}
                </dt>
                <dd>{money(band.illustration_minor, terms.currency)}</dd>
              </div>
            ))}
          <div className="total" style={{ display: "contents" }}>
            <dt>{ui.exampleTotal}</dt>
            <dd>{money(terms.illustration_total_minor, terms.currency)}</dd>
          </div>
        </dl>
        <p className="footnote">{ui.illustrationNote}</p>
      </section>
      <section aria-labelledby="flow-title">
        <h2 id="flow-title" className="section-title">
          {ui.flowTitle}
        </h2>
        <ul className="hierarchy">
          <li data-depth="0">Customer subscription</li>
          <li data-depth="1">
            Ambassador
            <span className="depth-note">Direct commission</span>
          </li>
          {terms.network_overrides.map((override, index) => (
            <li key={override.role} data-depth={String(index + 2)}>
              {roleName(override.role, false)}
              <span className="depth-note">
                Network override · {percent(override.rate_basis_points)}
              </span>
            </li>
          ))}
        </ul>
      </section>
      {journey.founding && (
        <section aria-labelledby="components-title">
          <h2 id="components-title" className="section-title">
            {ui.componentsTitle}
          </h2>
          <Cards
            items={terms.component_keys.map(
              (key) => compensationComponents[key] ?? { title: key },
            )}
            vars={vars}
          />
        </section>
      )}
    </>
  );
}

function Workspace({
  vars,
  currency,
  componentKeys,
}: {
  readonly vars: Readonly<Record<string, string>>;
  readonly currency: string;
  readonly componentKeys: readonly string[];
}): React.JSX.Element {
  void currency;
  return (
    <>
      <p className="demo-flag">{ui.demonstrationData}</p>
      <ul className="metrics" aria-label="Example network metrics">
        {workspaceDemo.metrics.map((metric) => (
          <li className="metric" key={metric.title}>
            <span>{metric.title}</span>
            <strong>{metric.detail}</strong>
          </li>
        ))}
      </ul>
      <section aria-labelledby="priorities-title">
        <h2 id="priorities-title" className="section-title">
          {workspaceDemo.prioritiesTitle}
        </h2>
        <Cards items={workspaceDemo.priorities} vars={vars} />
      </section>
      <section aria-labelledby="network-demo-title">
        <h2 id="network-demo-title" className="section-title">
          {workspaceDemo.networkTitle}
        </h2>
        <ul className="hierarchy">
          {workspaceDemo.network.map((node, index) => (
            <li key={node.title} data-depth={index === 0 ? "0" : "1"}>
              {interpolate(node.title, vars)}
              {node.detail !== undefined && (
                <span className="depth-note">{node.detail}</span>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section aria-labelledby="earnings-title">
        <h2 id="earnings-title" className="section-title">
          {workspaceDemo.earningsTitle}
        </h2>
        <ul className="chips">
          {componentKeys.map((key) => (
            <li className="chip" key={key}>
              {compensationComponents[key]?.title ?? key}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function GrowthAi({
  vars,
}: {
  readonly vars: Readonly<Record<string, string>>;
}): React.JSX.Element {
  return (
    <div className="ai-demo">
      <p className="ai-question">{growthAiDemo.question}</p>
      <ol>
        {growthAiDemo.answers.map((item) => (
          <li key={item.title}>
            <strong>{interpolate(item.title, vars)}</strong>
            <span>{interpolate(item.detail, vars)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function StepView({ journey, step, vars }: Props): React.JSX.Element {
  const copy = stepCopy[step.key];
  if (copy === undefined) {
    return <p>{ui.errorGeneric}</p>;
  }
  return (
    <>
      {copy.eyebrow !== undefined && (
        <p className="eyebrow">{interpolate(copy.eyebrow, vars)}</p>
      )}
      <h1>{interpolate(copy.title, vars)}</h1>
      {copy.lede !== undefined && (
        <p className="lede">{interpolate(copy.lede, vars)}</p>
      )}

      {step.key === "private-invitation" && (
        <div className="role-card">
          <p className="role-label">Role</p>
          <p className="role-name">
            {roleName(journey.network_role, journey.founding)}
          </p>
          <p className="role-territory">
            {territoryName(journey.territory_code)}
            {vars.Segment !== undefined && vars.Segment !== ""
              ? ` · ${vars.Segment}`
              : ""}
          </p>
        </div>
      )}

      {copy.body !== undefined && (
        <div className="prose">
          {copy.body.map((paragraph) => (
            <p key={paragraph}>{interpolate(paragraph, vars)}</p>
          ))}
        </div>
      )}

      {copy.highlight !== undefined && (
        <p className="highlight">{interpolate(copy.highlight, vars)}</p>
      )}

      {step.key === "types-and-grades" && (
        <>
          <section aria-labelledby="types-title">
            <h2 id="types-title" className="section-title">
              Ambassador types
            </h2>
            <Cards
              items={journey.program.ambassador_types.map(
                (key) => ambassadorTypes[key] ?? { title: key },
              )}
              vars={vars}
            />
          </section>
          <section aria-labelledby="grades-title">
            <h2 id="grades-title" className="section-title">
              {copy.listTitle ?? "Grades"}
            </h2>
            <ul className="ladder">
              {journey.program.ambassador_grades.map((key) => (
                <li className="rung" key={key}>
                  <span className="rung-key">
                    {ambassadorGrades[key]?.detail ?? key}
                  </span>
                  <span>{ambassadorGrades[key]?.title ?? key}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      {step.key === "network" && (
        <Network vars={vars} roles={journey.program.network_roles} />
      )}

      {step.key === "economics" && <Economics journey={journey} vars={vars} />}

      {step.key === "growth-workspace" && (
        <Workspace
          vars={vars}
          currency={journey.program.compensation.currency}
          componentKeys={journey.program.compensation.component_keys}
        />
      )}

      {step.key === "ai-and-automation" && <GrowthAi vars={vars} />}

      {copy.list !== undefined && step.key !== "types-and-grades" && (
        <section aria-labelledby={`${step.key}-list`}>
          {copy.listTitle !== undefined && (
            <h2 id={`${step.key}-list`} className="section-title">
              {interpolate(copy.listTitle, vars)}
            </h2>
          )}
          <Cards items={copy.list} vars={vars} />
        </section>
      )}

      {copy.secondaryList !== undefined && (
        <section aria-labelledby={`${step.key}-secondary`}>
          {copy.secondaryTitle !== undefined && (
            <h2 id={`${step.key}-secondary`} className="section-title">
              {interpolate(copy.secondaryTitle, vars)}
            </h2>
          )}
          <Cards items={copy.secondaryList} vars={vars} />
        </section>
      )}

      {copy.closing !== undefined && (
        <div className="prose">
          {copy.closing.map((paragraph) => (
            <p key={paragraph}>{interpolate(paragraph, vars)}</p>
          ))}
        </div>
      )}

      {step.key === "private-invitation" && (
        <p className="meta">
          {ui.invitationMeta.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </p>
      )}
    </>
  );
}
